import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";

/**
 * First-visit cookie consent banner.
 *
 * Persists the choice in localStorage so the banner only shows once per browser.
 * We don't set any tracking cookies ourselves — this is a baseline compliance
 * banner declaring that essential cookies (auth session) are used.
 *
 * Choices:
 *   • "Sounds good"  → consent = "accepted"
 *   • "Just essentials" → consent = "essentials_only"
 *
 * Both choices dismiss the banner; today there's no analytics gated behind it,
 * but the storage key is in place if/when GA or similar is added later.
 */
const STORAGE_KEY = "appistry.cookie_consent.v1";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay so it doesn't slam in on page load — feels less aggressive
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
      } catch {
        setVisible(true); // localStorage blocked → show every time (rare)
      }
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const decide = (choice) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() })); } catch (e) { void e; }
    setVisible(false);
  };

  if (!visible) return null;

  // Hide on admin pages — they're for logged-in staff who've already consented,
  // and the bottom-left position covers the /admin/login submit button on small viewports.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 md:bottom-5 md:left-5 md:right-auto md:max-w-md z-[90]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent"
    >
      <div className="bg-zinc-950 text-white border border-zinc-800 shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-3 mb-3">
          <Cookie size={22} weight="fill" className="text-[hsl(var(--appistry-signal))] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="overline text-zinc-400 mb-1">Cookies, briefly</p>
            <h3 className="text-base font-bold tracking-tight mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              We keep this light.
            </h3>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Essential cookies only — kept just long enough to remember where you got to if you're filling out the IdeaLab. No third-party tracking, no advertising pixels, no analytics. See our{" "}
              <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-white">privacy notice</Link> for the full picture.
            </p>
          </div>
          <button
            onClick={() => decide("dismissed")}
            aria-label="Close"
            className="text-zinc-500 hover:text-white p-1 -mt-1 -mr-1"
            data-testid="cookie-consent-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={() => decide("accepted")}
            className="bg-[hsl(var(--appistry-signal))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90"
            data-testid="cookie-consent-accept"
          >
            Sounds good
          </button>
          <button
            onClick={() => decide("essentials_only")}
            className="bg-transparent border border-zinc-700 text-zinc-200 px-4 py-2 text-sm font-semibold hover:border-zinc-500"
            data-testid="cookie-consent-essentials"
          >
            Just essentials
          </button>
        </div>
      </div>
    </div>
  );
}
